import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  PayScaleTypeEnum,
  payScaleTypeEnumMapping
} from '../../../lib/types/enums/staff-pay-scale/pay-scale-type.enum'
import { StaffPayScaleFilters } from '../../../lib/types/models/staff-pay-scale/staff-pay-cale'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: StaffPayScaleFilters
  onFilterChange: (filter: StaffPayScaleFilters) => void
}

export function StaffPayScalesFilter({ filter, onFilterChange }: Props) {
  const { t } = useTranslate()

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  function setFilter<K extends keyof StaffPayScaleFilters>(
    key: K,
    value: StaffPayScaleFilters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  return (
    <div className={'flex flex-col gap-3'}>
      <div />
      <SelectInput
        label={'Type de rémunération'}
        options={Object.values(PayScaleTypeEnum).map((type) => ({
          label: payScaleTypeEnumMapping[type].label,
          value: type
        }))}
        selectedOption={getSelectedEnumOption(filter?.type, payScaleTypeEnumMapping)}
        onChange={(option) => setFilter('type', option)}
      />

      <SelectInput
        label={'Poste'}
        options={Object.values(StaffPositionEnum)
          .filter((type) => type !== StaffPositionEnum.AAT)
          .map((type) => ({ label: t(staffPositionEnumMapping[type]), value: type }))}
        selectedOption={getSelectedEnumOption(filter?.position, staffPositionEnumMapping, t)}
        onChange={(option) => setFilter('position', option)}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Qualifications"
        options={Object.values(StaffQualificationEnum).map((value) => ({
          label: t(staffQualificationEnumMapping[value].label),
          value
        }))}
        onChange={(options) => setFilter('qualifications', options)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Formules"
        options={
          formulas?.data.map((formula) => ({
            label: getTranslatedName(formula),
            value: formula.id
          })) ?? []
        }
        onChange={(options) => setFilter('formulas', options)}
        selectedOptions={filter?.formulas?.map((formula) => ({
          label: getTranslatedName(formulas?.data.find((f) => f.id === formula)),
          value: formula
        }))}
      />

      <MultiSelectInput
        searchable
        variant="warning"
        label="Périodes"
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.name
          })) ?? []
        }
        onChange={(options) => setFilter('periods', options)}
        selectedOptions={filter?.periods?.map((period) => ({
          label: period,
          value: period
        }))}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Pays"
        options={
          countries?.data.map((country) => ({
            label: country.name,
            value: country.name
          })) ?? []
        }
        onChange={(options) => setFilter('countries', options)}
        selectedOptions={filter?.countries?.map((country) => ({
          label: country,
          value: country
        }))}
      />
    </div>
  )
}
